import type { UserGender } from '../api/types';
import type { AppLocale } from './messages';

/**
 * Шаблоны поздравления с днём рождения (заказ владельца 2026-08-29).
 *
 * <p>Своим файлом, а не двумя десятками ключей в `messages.ts`. Причина не в объёме: шаблон —
 * это не подпись кнопки, а <b>заготовка чужого текста</b>. Она склоняется по роду именинника
 * («дорогой» / «дорогая»), подставляет имя и возраст, а на языках без рода эти же три
 * варианта сливаются в один. Разложенное по плоским ключам, всё это превратилось бы в
 * `birthdayTemplate3Female` и в правило «не забудь про четвёртый язык», которое однажды
 * забудут.
 *
 * <p>Возраст подставляется только там, где он есть: «поздравляю с 30-летием» у человека,
 * скрывшего дату рождения, — это подстановка `undefined` в чужое поздравление.
 *
 * <p>Текст остаётся правимым: шаблон кладётся в поле ввода, а не уходит сам. Готовое
 * поздравление, отправленное нажатием одной кнопки, — это открытка от приложения, а не от
 * человека, и адресат разницу увидит.
 */

type BirthdayGreetingTemplate = {
  /** Устойчивый ключ: по нему запоминается «этим я уже поздравлял», а не по тексту. */
  id: string;
  text: string;
};

type Ctx = {
  name: string;
  /** Сколько исполнилось; отсутствует, если дата рождения не даёт года. */
  age?: number | null;
  gender?: UserGender | null;
};

/**
 * Род по полу; «не указан» идёт по нейтральной форме, а не по мужской.
 *
 * <p>Сравнение, а не `switch` по всем значениям: с сервера может прийти снятый `OTHER`
 * (профили, заполненные до решения владельца 2026-08-29 убрать этот вариант), и он обязан
 * попасть в нейтральную форму, а не уронить разбор.
 */
function pick(gender: UserGender | null | undefined, male: string, female: string, neutral: string): string {
  if (gender === 'MALE') return male;
  if (gender === 'FEMALE') return female;
  return neutral;
}

function ru(ctx: Ctx): BirthdayGreetingTemplate[] {
  const dear = pick(ctx.gender, 'Дорогой', 'Дорогая', 'Дорогой человек');
  const out: BirthdayGreetingTemplate[] = [
    { id: 'warm', text: `${dear} ${ctx.name}, с днём рождения! Пусть всё задуманное сбывается.` },
    { id: 'short', text: `С днём рождения, ${ctx.name}! 🎉` },
    {
      id: 'wishes',
      text: `${ctx.name}, поздравляю с днём рождения! Здоровья, сил и хороших людей рядом.`,
    },
    {
      id: 'friendly',
      text: `С праздником, ${ctx.name}! Спасибо, что ты есть — и пусть этот год будет твоим.`,
    },
  ];
  if (typeof ctx.age === 'number') {
    out.splice(2, 0, {
      id: 'age',
      text: `${ctx.name}, с ${ctx.age}-летием! Пусть этот возраст будет одним из лучших.`,
    });
  }
  return out;
}

function en(ctx: Ctx): BirthdayGreetingTemplate[] {
  const out: BirthdayGreetingTemplate[] = [
    { id: 'warm', text: `Dear ${ctx.name}, happy birthday! May everything you hope for come true.` },
    { id: 'short', text: `Happy birthday, ${ctx.name}! 🎉` },
    { id: 'wishes', text: `${ctx.name}, happy birthday! Health, energy and good people around you.` },
    { id: 'friendly', text: `Happy birthday, ${ctx.name}! Glad you are around — make this year yours.` },
  ];
  if (typeof ctx.age === 'number') {
    out.splice(2, 0, { id: 'age', text: `${ctx.name}, happy ${ctx.age}th! May this year be one of the best.` });
  }
  return out;
}

function sr(ctx: Ctx): BirthdayGreetingTemplate[] {
  const dear = pick(ctx.gender, 'Dragi', 'Draga', 'Dragi prijatelju');
  const out: BirthdayGreetingTemplate[] = [
    { id: 'warm', text: `${dear} ${ctx.name}, srećan rođendan! Neka ti se ostvari sve što želiš.` },
    { id: 'short', text: `Srećan rođendan, ${ctx.name}! 🎉` },
    { id: 'wishes', text: `${ctx.name}, srećan rođendan! Zdravlja, snage i dobrih ljudi oko tebe.` },
    { id: 'friendly', text: `Srećan ti dan, ${ctx.name}! Neka ova godina bude tvoja.` },
  ];
  if (typeof ctx.age === 'number') {
    out.splice(2, 0, {
      id: 'age',
      text: `${ctx.name}, srećan ${ctx.age}. rođendan! Neka bude jedna od najlepših godina.`,
    });
  }
  return out;
}

function fr(ctx: Ctx): BirthdayGreetingTemplate[] {
  const dear = pick(ctx.gender, 'Cher', 'Chère', 'Cher ami');
  const out: BirthdayGreetingTemplate[] = [
    { id: 'warm', text: `${dear} ${ctx.name}, joyeux anniversaire ! Que tous tes souhaits se réalisent.` },
    { id: 'short', text: `Joyeux anniversaire, ${ctx.name} ! 🎉` },
    { id: 'wishes', text: `${ctx.name}, joyeux anniversaire ! Santé, énergie et de belles personnes autour de toi.` },
    { id: 'friendly', text: `Bonne fête, ${ctx.name} ! Que cette année soit la tienne.` },
  ];
  if (typeof ctx.age === 'number') {
    out.splice(2, 0, {
      id: 'age',
      text: `${ctx.name}, joyeux ${ctx.age} ans ! Que ce soit une de tes meilleures années.`,
    });
  }
  return out;
}

const BY_LOCALE: Record<AppLocale, (ctx: Ctx) => BirthdayGreetingTemplate[]> = { ru, en, sr, fr };

/**
 * Шаблоны на языке интерфейса поздравляющего, а не именинника: пишет их он, и читать своё
 * поздравление перед отправкой он должен на своём языке.
 */
export function birthdayGreetingTemplates(locale: AppLocale, ctx: Ctx): BirthdayGreetingTemplate[] {
  const build = BY_LOCALE[locale] ?? en;
  const name = ctx.name.trim();
  return build({ ...ctx, name: name.length > 0 ? name : '' })
    // Пустое имя оставляет двойные пробелы и запятую в начале — чистим здесь, а не в каждом
    // шаблоне: имя пустым не бывает, но карточка контакта приходит с сервера, и однажды придёт.
    .map((t) => ({ ...t, text: t.text.replace(/\s{2,}/g, ' ').replace(/^,\s*/, '').trim() }));
}
